import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { GodModeIdleDialogComponent, GodModeIdleDialogResult } from '../../shared/god-mode-idle-dialog/god-mode-idle-dialog.component';
import { baseUrl } from '../../shared/constants';
import { UserAccessService } from './user-access.service';

export const GOD_MODE_APPROVAL_PENDING_MESSAGE =
  'Your God Mode access request is awaiting approval. Please wait for an administrator to approve your access.';

@Injectable({ providedIn: 'root' })
export class GodModeService implements OnDestroy {
  private static readonly IDLE_TIMEOUT_MS = 60_000;
  private static readonly WARNING_SECONDS = 10;
  private static readonly WARNING_AT_MS = GodModeService.IDLE_TIMEOUT_MS - GodModeService.WARNING_SECONDS * 1000;

  private readonly enabledSubject = new BehaviorSubject(false);
  readonly enabled$: Observable<boolean> = this.enabledSubject.asObservable().pipe(distinctUntilChanged());
  readonly eligible$: Observable<boolean>;

  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private warningDialogRef: MatDialogRef<GodModeIdleDialogComponent, GodModeIdleDialogResult> | null = null;
  private enabling = false;
  private lastActivityResetAt = 0;

  constructor(
    private readonly access: UserAccessService,
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly zone: NgZone,
  ) {
    this.eligible$ = this.access.isGlobalAdmin$.pipe(distinctUntilChanged());
    this.eligible$.subscribe((eligible) => {
      if (!eligible && this.enabled) {
        this.disable();
      }
    });
    this.zone.runOutsideAngular(() => document.addEventListener('mousemove', this.onMouseMove, { passive: true }));
  }

  get enabled(): boolean {
    return this.enabledSubject.value;
  }

  get eligible(): boolean {
    return this.access.isGlobalAdmin;
  }

  async enable(): Promise<void> {
    if (this.enabled || this.enabling) {
      return;
    }
    if (!this.eligible) {
      throw new Error('God mode requires the Azure AD ADMIN role.');
    }

    this.enabling = true;
    try {
      await firstValueFrom(this.http.post<void>(`${baseUrl}/god-mode/access`, null));
      this.enabledSubject.next(true);
      this.scheduleIdleTimeout();
    } catch (error) {
      if (this.isPendingApproval(error)) {
        throw new Error(GOD_MODE_APPROVAL_PENDING_MESSAGE);
      }
      throw error;
    } finally {
      this.enabling = false;
    }
  }

  disable(): void {
    this.clearTimers();
    this.enabledSubject.next(false);
    if (this.warningDialogRef) {
      this.warningDialogRef.close('leave');
      this.warningDialogRef = null;
    }
  }

  async getAdminProofToken(): Promise<string> {
    if (!this.enabled) {
      throw new Error('God mode is not enabled.');
    }
    return '';
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.onMouseMove);
    this.clearTimers();
  }

  private readonly onMouseMove = (): void => {
    if (!this.enabled || this.warningDialogRef) {
      return;
    }
    if (Date.now() - this.lastActivityResetAt < 1000) {
      return;
    }
    this.scheduleIdleTimeout();
  };

  private scheduleIdleTimeout(): void {
    this.clearTimers();
    this.lastActivityResetAt = Date.now();
    this.zone.runOutsideAngular(() => {
      this.warningTimer = setTimeout(() => this.zone.run(() => this.showIdleWarning()), GodModeService.WARNING_AT_MS);
      this.expiryTimer = setTimeout(() => this.zone.run(() => this.disable()), GodModeService.IDLE_TIMEOUT_MS);
    });
  }

  private showIdleWarning(): void {
    if (!this.enabled || this.warningDialogRef) {
      return;
    }

    this.warningDialogRef = this.dialog.open(GodModeIdleDialogComponent, {
      disableClose: true,
      closeOnNavigation: false,
      data: { seconds: GodModeService.WARNING_SECONDS },
      panelClass: 'god-mode-idle-warning-panel',
    });
    this.warningDialogRef.afterClosed().subscribe((result) => {
      this.warningDialogRef = null;
      if (result === 'stay' && this.enabled) {
        this.scheduleIdleTimeout();
        return;
      }
      this.disable();
    });
  }

  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private isPendingApproval(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error !== null &&
      'message' in error.error &&
      typeof error.error.message === 'string' &&
      error.error.message === 'God Mode approval is pending. Please wait for an administrator to approve your access.'
    );
  }
}
